import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const lotKeys = {
  all: ['lots'],
  lists: () => [...lotKeys.all, 'list'],
  list: (filters) => [...lotKeys.lists(), filters],
  details: () => [...lotKeys.all, 'detail'],
  detail: (id) => [...lotKeys.details(), id],
};

export const useLots = (filters = {}) => {
  return useQuery({
    queryKey: lotKeys.list(filters),
    queryFn: async () => {
      const params = {};
      if (filters?.uuid_allotment) {
        params.uuid_allotment = filters.uuid_allotment;
      }
      if (filters?.only_associated || filters?.only_with_contract) {
        params.only_associated = true;
      }
      const response = await apiClient.get('lots', { params });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useLot = (id, options = {}) => {
  return useQuery({
    queryKey: lotKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`lots/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateLot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lotData) => {
      const response = await apiClient.post('lots', lotData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lotKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error) => {
      console.error('Error creating lot:', error);
    },
  });
};

export const useUpdateLot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, lotData }) => {
      const response = await apiClient.put(`lots/${id}`, lotData);
      return response.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: lotKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lotKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error) => {
      console.error('Error updating lot:', error);
    },
  });
};

export const useDeleteLot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`lots/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: lotKeys.lists() });
      queryClient.removeQueries({ queryKey: lotKeys.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error) => {
      console.error('Error deleting lot:', error);
    },
  });
};
